import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ApplicationStatusDropdown } from "./ApplicationStatusDropdown";
import { CheckCircle2, XCircle, AlertTriangle, Target, Search, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface ResumeMatchResultsProps {
  result: any;
  savedJobId?: string;
  jobTitle?: string;
  companyName?: string;
  jobDescriptionText?: string;
  resumeStoragePath?: string;
}

function ScoreCircle({ label, score, threshold = 80 }: { label: string; score: number; threshold?: number }) {
  const pass = score >= threshold;
  const color = pass ? 'text-green-600' : 'text-red-600';
  const ringColor = pass ? 'border-green-400' : 'border-red-400';
  const bgColor = pass ? 'bg-green-50' : 'bg-red-50';

  return (
    <div className="flex flex-col items-center gap-2">
      <div className={cn("w-24 h-24 rounded-full border-4 flex items-center justify-center", ringColor, bgColor)}>
        <span className={cn("text-2xl font-bold", color)}>{score}%</span>
      </div>
      <span className="text-sm font-medium text-center">{label}</span>
      <Badge variant={pass ? "default" : "destructive"} className="text-xs">
        {pass ? 'Pass' : 'Fail'}
      </Badge>
    </div>
  );
}

function getCoverageColor(coverage: string) {
  if (coverage === 'Directly Addressed') return 'border-l-green-500 bg-green-50/30';
  if (coverage === 'Partially Addressed') return 'border-l-amber-500 bg-amber-50/30';
  return 'border-l-red-500 bg-red-50/30';
}

function getCoverageRatingBadge(rating: string) {
  const colors: Record<string, string> = {
    'Strong': 'bg-green-100 text-green-800',
    'Moderate': 'bg-amber-100 text-amber-800',
    'Weak': 'bg-orange-100 text-orange-800',
    'None': 'bg-red-100 text-red-800',
  };
  return colors[rating] || 'bg-muted text-muted-foreground';
}

function getImportanceBadge(importance: string) {
  if (importance === 'Critical') return 'bg-red-100 text-red-800 border-red-300';
  if (importance === 'Important') return 'bg-amber-100 text-amber-800 border-amber-300';
  return 'bg-muted text-muted-foreground';
}

export function ResumeMatchResults({ result, savedJobId, jobTitle, companyName, jobDescriptionText, resumeStoragePath }: ResumeMatchResultsProps) {
  const navigate = useNavigate();
  const ats = result.ats_match_score;
  const human = result.human_eye_score;
  const overallScore = ats && human ? Math.round((ats.score + human.score) / 2) : 0;
  const tv = result.tailoring_verdict;

  const verdictColor = tv?.verdict === 'Submit As Is'
    ? 'bg-green-50 border-green-400 text-green-800'
    : tv?.verdict === 'Tailor Before Submitting'
    ? 'bg-amber-50 border-amber-400 text-amber-800'
    : 'bg-red-50 border-red-400 text-red-800';

  return (
    <div className="space-y-6">
      {/* Status dropdown */}
      {savedJobId && (
        <div className="flex justify-end">
          <ApplicationStatusDropdown savedJobId={savedJobId} currentStatus="saved" />
        </div>
      )}

      {/* Score Circles */}
      <Card>
        <CardContent className="py-6">
          <div className="flex justify-center gap-8 flex-wrap">
            <ScoreCircle label="ATS Match" score={ats?.score || 0} />
            <ScoreCircle label="Human Eye" score={human?.score || 0} />
            <ScoreCircle label="Overall" score={overallScore} />
          </div>
          <p className="text-xs text-center text-muted-foreground mt-3">80% or higher required to pass</p>
        </CardContent>
      </Card>

      {/* Tailoring Verdict Banner */}
      {tv && (
        <Card className={cn("border-2", verdictColor)}>
          <CardContent className="py-5">
            <h2 className="text-xl font-bold mb-2">{tv.verdict}</h2>
            <p className="text-sm text-muted-foreground mb-3">{tv.verdict_reasoning}</p>
            <div className="flex gap-2 flex-wrap">
              <Badge variant={tv.ats_pass ? "default" : "destructive"} className="text-xs">
                ATS: {tv.ats_pass ? 'Pass' : 'Fail'}
              </Badge>
              <Badge variant={tv.human_eye_pass ? "default" : "destructive"} className="text-xs">
                Human Eye: {tv.human_eye_pass ? 'Pass' : 'Fail'}
              </Badge>
              <Badge variant={tv.all_problems_addressed ? "default" : "destructive"} className="text-xs">
                {tv.all_problems_addressed ? 'All Problems Addressed' : 'Gaps Found'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Company Problems Coverage */}
      {result.company_problems_coverage?.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Company Problems & Resume Coverage</h3>
          <div className="space-y-4">
            {result.company_problems_coverage.map((p: any) => (
              <Card key={p.rank} className={cn("border-l-4", getCoverageColor(p.resume_coverage))}>
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <span className="text-3xl font-bold text-muted-foreground/40">#{p.rank}</span>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold">{p.problem_title}</h4>
                        <Badge variant="outline" className="text-xs">{p.source}</Badge>
                        <Badge className={cn("text-xs", getCoverageRatingBadge(p.coverage_rating))}>{p.coverage_rating}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{p.problem_description}</p>
                      <div className="bg-muted/50 rounded p-2 text-sm">
                        <span className="font-medium">Evidence: </span>
                        <span className="text-muted-foreground">{p.coverage_evidence}</span>
                      </div>
                      {p.coverage_rating === 'Strong' ? (
                        <p className="text-sm text-green-700 flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" /> Well covered, no changes needed
                        </p>
                      ) : (
                        <p className="text-sm italic text-muted-foreground">{p.recommendation}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Missing Keywords */}
      {result.missing_keywords?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Missing Keywords</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {result.missing_keywords.map((kw: any, i: number) => (
                <Badge
                  key={i}
                  variant="outline"
                  className={cn("text-xs border", getImportanceBadge(kw.importance))}
                  title={`Add to: ${kw.where_to_add}. ${kw.context}`}
                >
                  {kw.keyword}
                </Badge>
              ))}
            </div>
            <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> Critical</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Important</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-muted-foreground/40" /> Nice to Have</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Weak Sections */}
      {result.weak_sections?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Weak Sections</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {result.weak_sections.map((ws: any, i: number) => (
              <div key={i} className="flex items-start gap-3 border-b last:border-0 pb-3 last:pb-0">
                <AlertTriangle className={cn("w-4 h-4 mt-0.5", ws.severity === 'High' ? 'text-red-500' : ws.severity === 'Medium' ? 'text-amber-500' : 'text-muted-foreground')} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{ws.section_name}</span>
                    <Badge variant="outline" className="text-xs">{ws.severity}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{ws.issue}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Top 3 Recommended Changes */}
      {result.top_recommended_changes?.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Top Recommended Changes</h3>
          <div className="grid gap-4 md:grid-cols-3">
            {result.top_recommended_changes.map((rc: any) => (
              <Card key={rc.rank} className="border-primary/20">
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-primary/30">#{rc.rank}</span>
                    <Badge className="bg-green-100 text-green-800 text-xs">
                      <ArrowUp className="w-3 h-3 mr-0.5" />+{rc.estimated_score_improvement} pts
                    </Badge>
                  </div>
                  <h4 className="font-semibold">{rc.change_title}</h4>
                  <p className="text-sm text-muted-foreground">{rc.change_description}</p>
                  <Badge variant="outline" className="text-xs">{rc.section_affected}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          className="flex-1 bg-gradient-to-r from-green-500 to-lime-500 hover:from-green-600 hover:to-lime-600 text-white"
          onClick={() => navigate('/resume-tailoring', {
            state: { jobTitle, companyName, jobDescription: jobDescriptionText, resumePath: resumeStoragePath }
          })}
        >
          <Target className="w-4 h-4 mr-2" /> Fix This Resume Now
        </Button>
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => navigate('/jobintel-360-reports', {
            state: { triggerReport: 'job_insider', jobTitle, companyName }
          })}
        >
          <Search className="w-4 h-4 mr-2" /> Run Full Job Intelligence Report
        </Button>
      </div>
    </div>
  );
}
